const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

export interface Episode {
  id: number
  moduleId: number
  number: number
  title: string
  description: string | null
  status?: 'draft' | 'published' | 'archived' | 'coming_soon'
  videoUrl: string | null
  workbookUrl: string | null
  workbookCoverUrl?: string | null
  thumbnailUrl?: string | null
  duration: number
  module: {
    id: number
    title: string
    description: string
    order: number
    courseId: number
    courseTitle: string
    /** Tipo do curso pai — back v198+. Optional pra compat. */
    courseType?: 'regular' | 'masterclass' | 'workshop' | 'trail' | 'drop'
    /** Total de episódios não-deletados do módulo — back v198+. */
    totalEpisodes?: number
  }
}

export interface EpisodeProgress {
  completed: boolean
  watchedDuration: number
  lastWatchedAt: Date
}

export interface EpisodeNavigation {
  hasNext: boolean
  hasPrevious: boolean
  nextEpisode: { moduleId: number; number: number } | null
  prevEpisode: { moduleId: number; number: number } | null
  prevHasQuiz?: boolean
}

export interface EpisodeResponse {
  episode: Episode
  progress: EpisodeProgress | null
  navigation: EpisodeNavigation
  hasQuiz: boolean
}

export const episodesService = {
  /**
   * Busca um episódio específico
   */
  async getEpisode(
    moduleId: number,
    episodeNumber: number,
    token?: string
  ): Promise<EpisodeResponse> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${API_URL}/episodes/${moduleId}/${episodeNumber}`, {
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Episódio não encontrado')
    }

    return response.json()
  },

  /**
   * Lista todos episódios de um módulo
   */
  async getModuleEpisodes(moduleId: number, token?: string) {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${API_URL}/modules/${moduleId}/episodes`, {
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error('Módulo não encontrado')
    }

    return response.json()
  },

  /**
   * Atualiza progresso do episódio
   */
  async updateProgress(
    episodeId: number,
    data: { completed?: boolean; watchedDuration?: number },
    token: string
  ): Promise<void> {
    const response = await fetch(`${API_URL}/episodes/${episodeId}/progress`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Erro ao atualizar progresso')
    }
  },
}
